import React, { useState, useEffect, useRef } from "react"
import classnames from "classnames"
import * as layouts from "layouts"
import * as sessions from "sessions"
import * as metasearch from "metasearch"
import * as uuid from "uuid"
import * as brands from "brands"
import * as api from "./api"
import * as httpx from "httpx"
import * as errors from "errors"
import * as timex from "timex"
import { SearchRequest } from "./brandguard.metrics_pb"
import { Bar, Line } from "charts"
import * as inputs from "inputs"
import * as metricsLayouts from "./layouts"
import * as filters from "./filters"
import { getDaysArray, toDayKey, capitalize, formatCategory, INFISO, NEGINFISO } from "./dates"

const eventTypeOptions = ["all", "image", "text", "document", "video"] as const
const exportMethodOptions = ["all", "export", "email"] as const

interface parsed {
	labels: string[]
	exportByDay: { [date: string]: number }
	emailByDay: { [date: string]: number }
	categories: Map<string, number>
}

function parsedInitial(): parsed {
	return {
		labels: [],
		exportByDay: {},
		emailByDay: {},
		categories: new Map(),
	}
}

function parse(
	r: api.FeedbackTrendMetricSearchResponse,
	dr: metasearch.DateRange,
	uid: string = uuid.NIL,
	eventTypeFilter: string = "all",
	exportMethodFilter: string = "all",
): parsed {
	const data = parsedInitial()

	getDaysArray(new Date(dr.oldest), new Date(dr.newest)).forEach((i) => {
		const day = toDayKey(i)
		data.labels.push(day)
		data.exportByDay[day] = 0
		data.emailByDay[day] = 0
	})

	;(r.items || []).forEach((e) => {
		if (uid !== uuid.NIL && e.profile_id !== uid) return
		if (eventTypeFilter !== "all" && e.event_type !== eventTypeFilter) return

		const day = toDayKey(new Date(e.range!.oldest))
		const exportCount = Number(BigInt(e.export_count || 0))
		const emailCount = Number(BigInt(e.email_count || 0))

		if (exportMethodFilter === "all" || exportMethodFilter === "export") {
			data.exportByDay[day] = (data.exportByDay[day] || 0) + exportCount
		}
		if (exportMethodFilter === "all" || exportMethodFilter === "email") {
			data.emailByDay[day] = (data.emailByDay[day] || 0) + emailCount
		}

		let categoryTotal = 0
		if (exportMethodFilter === "all") {
			categoryTotal = exportCount + emailCount
		} else if (exportMethodFilter === "export") {
			categoryTotal = exportCount
		} else {
			categoryTotal = emailCount
		}

		const existing = data.categories.get(e.category) || 0
		data.categories.set(e.category, existing + categoryTotal)
	})

	return data
}

export default function Display(): JSX.Element {
	const brand = brands.caching.useCached()
	const bearertoken = sessions.useToken()

	const [loading, setLoading] = useState(true)
	const [cause, setCause] = useState(undefined as JSX.Element | undefined)

	const [req, setReq] = useState(
		api.SearchZero({
			brand_id: brand.id,
			range: metasearch.dateranges.days(30),
		}) as SearchRequest,
	)

	const [chartData, setChartData] = useState(parsedInitial())
	const [currentUser, setCurrentUser] = useState(filters.zeroUser)
	const [response, setResponse] = useState({} as api.FeedbackTrendMetricSearchResponse)
	const [eventTypeFilter, setEventTypeFilter] = useState("all")
	const [exportMethodFilter, setExportMethodFilter] = useState("all")
	const currentUserRef = useRef(currentUser)
	currentUserRef.current = currentUser
	const eventTypeFilterRef = useRef(eventTypeFilter)
	eventTypeFilterRef.current = eventTypeFilter
	const exportMethodFilterRef = useRef(exportMethodFilter)
	exportMethodFilterRef.current = exportMethodFilter

	const reparse = (
		resp: api.FeedbackTrendMetricSearchResponse,
		range: metasearch.DateRange,
		uid: string,
		evtFilter: string,
		expFilter: string,
	) => {
		setChartData(parse(resp, range, uid, evtFilter, expFilter))
	}

	const updateCurrentUser = (user: filters.user): void => {
		setCurrentUser(user)
		reparse(response, req.range!, user.key, eventTypeFilter, exportMethodFilter)
	}

	const updateEventTypeFilter = (t: string): void => {
		setEventTypeFilter(t)
		reparse(response, req.range!, currentUser.key, t, exportMethodFilter)
	}

	const updateExportMethodFilter = (m: string): void => {
		setExportMethodFilter(m)
		reparse(response, req.range!, currentUser.key, eventTypeFilter, m)
	}

	useEffect(() => {
		if (req.brand_id === uuid.NIL || req.range?.oldest === NEGINFISO || req.range?.newest === INFISO) return
		setLoading(true)
		const retry = httpx.autoretry()
		const p = retry
			.wrap(() => api.search_feedback_trends(req, bearertoken))
			.then((resp) => {
				setResponse(resp)
				reparse(resp, req.range!, currentUserRef.current.key, eventTypeFilterRef.current, exportMethodFilterRef.current)
				setCause(undefined)
			})
			.catch(httpx.errors.cancellation(() => undefined))
			.catch(
				httpx.errors.forbidden((cause) => {
					console.warn("insufficient privileges unable to view metrics", cause)
					setCause(
						<layouts.containers.box styled m="auto">
							<errors.Textual>you do not have permission to view metrics</errors.Textual>
						</layouts.containers.box>,
					)
				}),
			)
			.catch((c: unknown) => {
				console.error("unable to retrieve metrics", c)
				setCause(
					<layouts.containers.box styled m="auto" width="100%" height="100%">
						<errors.Textual onClick={() => setCause(undefined)}>unable to retrieve metrics</errors.Textual>
					</layouts.containers.box>,
				)
			})
			.finally(() => setLoading(false))
		return p.cancel
	}, [req])

	const sorted = Array.from(chartData.categories.entries()).sort((a, b) => b[1] - a[1])
	const barLabels = sorted.map(([cat]) => formatCategory(cat))
	const barValues = sorted.map(([, count]) => count)

	const barOptions = {
		indexAxis: "y",
		maintainAspectRatio: false,
		scales: {
			x: {
				min: 0,
				ticks: { color: layouts.theme.colors.grey.light30 },
				title: {
					display: true,
					text: "Export Volume",
					color: layouts.theme.colors.grey.light30,
					font: { size: 14 },
				},
				grid: { display: false },
			},
			y: {
				ticks: { color: layouts.theme.colors.grey.light30, font: { size: 12 } },
				grid: { display: false },
			},
		},
		layout: { padding: 20 },
		plugins: { legend: { display: false } },
	} as const

	const barData = {
		labels: barLabels,
		datasets: [
			{
				data: barValues,
				backgroundColor: layouts.theme.colors.charts.violet.alpha,
				borderColor: layouts.theme.colors.charts.violet.base,
				borderWidth: 1,
			},
		],
	}

	const lineOptions = {
		maintainAspectRatio: true,
		scales: {
			x: {
				ticks: { color: layouts.theme.colors.grey.light30 },
				grid: { display: false },
			},
			y: {
				type: "linear" as const,
				display: true,
				position: "left" as const,
				min: 0,
				ticks: { color: layouts.theme.colors.grey.light30, maxTicksLimit: 5 },
				title: {
					display: true,
					text: "Volume",
					color: layouts.theme.colors.grey.light30,
					font: { size: 14 },
					padding: 0,
					align: "center" as const,
				},
				grid: { display: false },
			},
		},
		layout: { padding: 20 },
		elements: { point: { radius: 0, pointHoverRadius: 5 } },
		plugins: {
			legend: {
				position: "top" as const,
				align: "end" as const,
				labels: { usePointStyle: true, boxWidth: 50, font: { size: 10, weight: "bold" as const } },
			},
		},
	}

	const lineDatasets = []
	if (exportMethodFilter === "all" || exportMethodFilter === "export") {
		lineDatasets.push({
			fill: true,
			label: "Exports",
			data: chartData.labels.map((day) => chartData.exportByDay[day] || 0),
			borderColor: layouts.theme.colors.charts.blue.base,
			backgroundColor: layouts.theme.colors.charts.blue.alpha,
			tension: 0.2,
		})
	}
	if (exportMethodFilter === "all" || exportMethodFilter === "email") {
		lineDatasets.push({
			fill: true,
			label: "Emails",
			data: chartData.labels.map((day) => chartData.emailByDay[day] || 0),
			borderColor: layouts.theme.colors.charts.green.base,
			backgroundColor: layouts.theme.colors.charts.green.alpha,
			tension: 0.2,
		})
	}

	const lineData = {
		labels: chartData.labels,
		datasets: lineDatasets,
	}

	const barHeight = Math.max(300, sorted.length * 40)

	return (
		<layouts.loading.screen loading={loading} height="100%">
			<errors.overlay cause={cause} flexDirection="column" flex="1">
				<layouts.containers.flex>
					<inputs.calendars.DateRangeOneDatepicker
						className={metricsLayouts.StyledDatePicker}
						placeholderText="Date Range"
						minStartDate={new Date(timex.from.iso(req.range?.oldest).minus(timex.duration.days(180)).toISO())}
						maxEndDate={new Date()}
						startDate={new Date(req.range?.oldest || NEGINFISO)}
						endDate={new Date(req.range?.newest || INFISO)}
						changeStart={(d: Date) => {
							setReq((prevState) => ({ ...prevState, range: { newest: INFISO, oldest: timex.from.date(d).toISO() } }))
						}}
						changeEnd={(d: Date) => {
							setReq((prevState) => ({
								...prevState,
								range: { ...prevState.range, newest: timex.from.date(d).toISO() },
							}))
						}}
					/>

					<layouts.containers.flex mt="3px" ml="10px" fontWeight="600">
						{req.range?.oldest !== NEGINFISO &&
							req.range?.newest !== INFISO &&
							`${timex.from
								.iso(req.range?.newest)
								.diff(timex.from.iso(req.range?.oldest), "days")
								.days.toFixed()} day(s)`}
					</layouts.containers.flex>
					<filters.ApprovalFilter
						req={req}
						setReq={setReq}
						currentUser={currentUser}
						setCurrentUser={updateCurrentUser}
					/>
				</layouts.containers.flex>

				<layouts.containers.flex mb="10px" ml="10px">
					{eventTypeOptions.map((t) => (
						<layouts.containers.flex
							key={t}
							px="12px"
							py="4px"
							mr="8px"
							borderRadius="4px"
							cursor="pointer"
							fontSize="13px"
							fontWeight={eventTypeFilter === t ? "700" : "400"}
							color={eventTypeFilter === t ? layouts.theme.colors.white : layouts.theme.colors.grey.dark50}
							background={eventTypeFilter === t ? layouts.theme.colors.blue.blue : layouts.theme.colors.grey.dark50alpha05}
							onClick={() => updateEventTypeFilter(t)}
						>
							{capitalize(t)}
						</layouts.containers.flex>
					))}
					<layouts.containers.flex mx="10px" my="4px" borderRight={layouts.theme.borders.grey.dark50alphamedium} />
					{exportMethodOptions.map((m) => (
						<layouts.containers.flex
							key={m}
							px="12px"
							py="4px"
							mr="8px"
							borderRadius="4px"
							cursor="pointer"
							fontSize="13px"
							fontWeight={exportMethodFilter === m ? "700" : "400"}
							color={exportMethodFilter === m ? layouts.theme.colors.white : layouts.theme.colors.grey.dark50}
							background={exportMethodFilter === m ? layouts.theme.colors.blue.blue : layouts.theme.colors.grey.dark50alpha05}
							onClick={() => updateExportMethodFilter(m)}
						>
							{capitalize(m)}
						</layouts.containers.flex>
					))}
				</layouts.containers.flex>

				<layouts.containers.box
					className="metrics"
					width="100%"
					background={layouts.theme.colors.white}
					overflowY="auto"
				>
					<layouts.containers.box
						className={classnames("primary", metricsLayouts.hover)}
						height={`${barHeight}px`}
						maxHeight="600px"
					>
						<Bar options={barOptions} data={barData} />
					</layouts.containers.box>

					<layouts.containers.box className={classnames("primary", metricsLayouts.hover)} height="512px" maxHeight="512px" mt="20px">
						<Line options={lineOptions} data={lineData} width={100} />
					</layouts.containers.box>
				</layouts.containers.box>
			</errors.overlay>
		</layouts.loading.screen>
	)
}
